import { useMemo } from 'react';
import {
    InformationCircleIcon,
    KeyIcon,
    MapIcon,
    RectangleStackIcon,
    Square3Stack3DIcon
} from '@heroicons/react/24/outline';
import type { CatalogPermission, CatalogRole, RouteInfo } from '../../../services/iam';
import { shortRoleKey } from '../iamLookups';
import { Chain } from '../primitives/Chain';
import { MethodTag } from '../primitives/MethodTag';
import { PermName } from '../primitives/PermName';
import { AccessCard } from '../primitives/AccessCard';
import { PermCountPill } from './PermCountPill';

interface RoleReference<T extends CatalogRole> {
    readonly role: T;
    readonly chains: readonly (readonly string[])[];
}

// Generic over `kind` so both callers get back a properly narrowed `role`.
function referencesContaining<K extends CatalogRole['kind']>(
    permissionId: string,
    roles: readonly CatalogRole[],
    kind: K
): RoleReference<Extract<CatalogRole, { kind: K }>>[] {
    return roles
        .filter((role): role is Extract<CatalogRole, { kind: K }> => role.kind === kind)
        .map((role) => {
            const entry = role.flattened.find((f) => f.permission === permissionId);
            return entry ? { role, chains: entry.chains } : null;
        })
        .filter((ref): ref is RoleReference<Extract<CatalogRole, { kind: K }>> => ref !== null);
}

interface ChainListProps {
    chains: readonly (readonly string[])[];
    /** Passed to every `Chain` as its terminal node so each trace reads through to this permission. */
    permission: string;
    group: string;
}

// Shortest chain at full opacity; further chains dimmed underneath as "also".
function ChainList({ chains, permission, group }: ChainListProps) {
    const [primary, ...rest] = chains;
    return (
        <>
            {primary && (
                <Chain chain={primary} permission={permission} group={group} className="pl-1" />
            )}
            {rest.map((chain, index) => (
                <div key={index} className="flex items-center gap-1.5 pl-1 opacity-50">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        also
                    </span>
                    <Chain chain={chain} permission={permission} group={group} />
                </div>
            ))}
        </>
    );
}

interface PermissionDetailPanelProps {
    permission: CatalogPermission;
    roles: readonly CatalogRole[];
    routes: readonly RouteInfo[];
    onSelectRole: (key: string) => void;
}

export function PermissionDetailPanel({
    permission,
    roles,
    routes,
    onSelectRole
}: PermissionDetailPanelProps) {
    const bundleRefs = useMemo(
        () => referencesContaining(permission.id, roles, 'bundle'),
        [permission.id, roles]
    );
    const roleRefs = useMemo(
        () => referencesContaining(permission.id, roles, 'role'),
        [permission.id, roles]
    );
    const enforcingRoutes = useMemo(
        () => routes.filter((route) => route.permission === permission.id),
        [routes, permission.id]
    );
    const wired = permission.routeCount > 0;

    return (
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide flex flex-col gap-3.5 pr-1">
            {/* Card 1: the permission itself. The three pills mirror the cards below. */}
            <AccessCard
                icon={KeyIcon}
                title={
                    <PermName
                        permission={permission.id}
                        group={permission.group}
                        wired={wired}
                        showState
                    />
                }
                action={
                    <div className="flex items-center gap-1.5">
                        <PermCountPill noun="bundle" count={bundleRefs.length} />
                        <PermCountPill noun="role" count={roleRefs.length} />
                        <PermCountPill noun="route" count={enforcingRoutes.length} />
                    </div>
                }
            />

            {/* Card 2: structural - bundles that include this permission (never grantable). */}
            <AccessCard
                icon={Square3Stack3DIcon}
                title="Bundles that contain it"
                count={bundleRefs.length}
                noBodyPadding
            >
                {bundleRefs.length === 0 ? (
                    <p className="text-sm text-gray-400 px-3.5 py-4">
                        No bundle includes this permission.
                    </p>
                ) : (
                    bundleRefs.map(({ role, chains }) => (
                        <div
                            key={role.key}
                            className="flex flex-col gap-1 py-2.5 px-3.5 border-b border-gray-100 last:border-b-0"
                        >
                            <button
                                type="button"
                                onClick={() => onSelectRole(role.key)}
                                className="text-[12px] font-mono font-semibold italic text-violet-700 hover:underline text-left"
                            >
                                {shortRoleKey(role.key)}
                            </button>
                            <ChainList
                                chains={chains}
                                permission={permission.id}
                                group={permission.group}
                            />
                        </div>
                    ))
                )}
            </AccessCard>

            {/* Card 3: operational - roles that contain it (what's actually grantable). */}
            <AccessCard
                icon={RectangleStackIcon}
                title="Roles that contain it"
                count={roleRefs.length}
                noBodyPadding
            >
                {roleRefs.length === 0 ? (
                    <p className="text-sm text-gray-400 px-3.5 py-4">
                        No role confers this permission.
                    </p>
                ) : (
                    roleRefs.map(({ role, chains }) => (
                        <div
                            key={role.key}
                            className="flex flex-col gap-1 py-2.5 px-3.5 border-b border-gray-100 last:border-b-0"
                        >
                            <button
                                type="button"
                                onClick={() => onSelectRole(role.key)}
                                className="flex flex-col items-start min-w-0 hover:underline text-left"
                            >
                                <span className="text-[12px] font-semibold text-gray-800 truncate">
                                    {role.title}
                                </span>
                            </button>
                            <ChainList
                                chains={chains}
                                permission={permission.id}
                                group={permission.group}
                            />
                        </div>
                    ))
                )}
            </AccessCard>

            {/* Card 4: routes that need it - empty here just means unmigrated, not broken. */}
            <AccessCard
                icon={MapIcon}
                title="Routes that need it"
                count={enforcingRoutes.length}
                noBodyPadding
            >
                {enforcingRoutes.length === 0 ? (
                    <div className="flex items-start gap-2.5 px-3.5 py-4">
                        <InformationCircleIcon className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                        <p className="text-sm text-gray-500">
                            No route enforces this permission yet.
                        </p>
                    </div>
                ) : (
                    enforcingRoutes.map((route) => (
                        <div
                            key={`${route.method}-${route.path}`}
                            className="flex items-center gap-2.5 py-2 px-3.5 border-b border-gray-100 last:border-b-0"
                        >
                            <MethodTag method={route.method} />
                            <span className="font-mono text-[12px] text-gray-700 truncate">
                                {route.path}
                            </span>
                        </div>
                    ))
                )}
            </AccessCard>
        </div>
    );
}
